import React, {
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useChatContext } from "stream-chat-react";
import Avatar from "../common/Avatar";
import BottomSheet from "../common/BottomSheet";
import { isClubAgentPitch } from "../../utils/chatContext";
import { chatActionService } from "../../services/api";
import AddChatMembersModal from "./AddChatMembersModal";
import LeaveGroupModal from "./LeaveGroupModal";
import ReassignAdminModal from "./ReassignAdminModal";
import SharedContentSection from "./SharedContentSection";

/*
  Participants rail (design Turn 5, frame 5a — Option A, persistent).

  The rail is the capability made visible: the counterpart pinned on top
  (untouchable, no actions), the viewer's club below with role tags and the
  ADMIN badge on the rights holder. Row "⋯" carries "Make admin", and the two
  rail buttons open the handover modals. The agent side gets a slim read-only
  variant (no actions) — participant changes reach them as chat events (5d).

  Data comes entirely from the channel: Stream user id === account_id, and the
  channel custom data carries `users[]`, `club_member_account_ids`,
  `club_admin_account_id`, `agent_account_id` and `_tp_custom_context`. No fetch
  is needed to render; the modals feature-detect the cid-based backend (D-008)
  and the rail hides the direct-channel controls on a 404.

  Wide screens only (hidden below lg); below that the same content is reachable
  through the bottom sheet, opened from the header's "Chat details" trigger.
*/

const stripMessagingPrefix = (value) =>
  value ? String(value).replace(/^messaging:/, "") : null;

const toId = (value) =>
  value !== undefined && value !== null ? String(value) : null;

// staff role_type arrives as an enum-ish token (e.g. "sporting_director").
// Present it human-readably; already-nice labels ("Team Manager") pass through.
const humanizeRole = (role) => {
  const raw = String(role || "").trim();
  if (!raw) return "";
  if (/[a-z]/.test(raw) && /[A-Z]/.test(raw) && !raw.includes("_")) {
    return raw; // already mixed-case, leave as authored
  }
  return raw
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

const pickName = (profile) =>
  profile?.name || profile?.full_name || profile?.email || "Member";

const pickAvatar = (profile) =>
  profile?.profile_picture_url || profile?.image || null;

const pickAgency = (profile) => profile?.agency_name || profile?.club_name || null;

const isAgentRole = (role) => String(role || "").toLowerCase().includes("agent");

const SECTION_LABEL_STYLE = {
  font: "600 9.5px ui-monospace,Menlo,monospace",
  color: "#98A2B3",
  letterSpacing: ".06em",
};

// Primary text token for a name line, stated explicitly on purpose. The rail
// mounts inside <StreamTheme> (Inbox.js), whose `.str-video` wrapper is styled
// `color: #ffffff !important` for the video call surface — every descendant that
// does not set its own colour inherits white and disappears on the white rail.
// Every other rail line already carries an explicit colour (SECTION_LABEL_STYLE,
// the role chip, the sub-label, SharedContentSection's titles at #101828), which
// is why only the names were invisible. Same token as the shared-content titles.
const NAME_COLOR = "#101828";

const ParticipantsRail = ({ channel: channelProp }) => {
  const { user, roles: userRoles, currentRole } = useSelector(
    (state) => state.auth || {},
  );

  // The rail mounts as a sibling of <Channel>, so it can't read
  // useChannelStateContext. It resolves the open thread exactly the way
  // <Channel channel={activeChannel}> does internally — explicit prop first,
  // then the ChatContext channel. A normal inbox-row click only sets the Stream
  // ChatContext channel (never useInbox.activeChannel), so the prop alone was
  // always null on click and the rail never rendered (parity #2).
  const { channel: contextChannel } = useChatContext();
  const channel = channelProp || contextChannel;

  const [showTooltip, setShowTooltip] = useState(false);
  // Feature-detect for cid-addressed (non-pitch) channels: null = unknown,
  // true = backend live, false = 404 (hide controls). Pitch channels are
  // always supported through the existing pitch endpoints.
  const [directControlsSupported, setDirectControlsSupported] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [reassignTargetId, setReassignTargetId] = useState(null);
  // Optimistic admin override so the ADMIN badge moves without a reload.
  const [adminOverride, setAdminOverride] = useState(null);

  // Below lg the rail is hidden and surfaces as a bottom sheet (5f).
  // The trigger is the header avatar-stack (owned by CustomChatHeader); it opens
  // the sheet by dispatching a window event, keeping the two files decoupled.
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  useEffect(() => {
    const open = () => setMobileSheetOpen(true);
    window.addEventListener("tp:threads:open-participants", open);
    return () => window.removeEventListener("tp:threads:open-participants", open);
  }, []);

  // Re-render when membership / metadata changes on the live channel object.
  // The Stream channel is mutated in place, so a state bump (not a prop change)
  // is what re-derives the participant lists below.
  const [tick, bumpTick] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    if (!channel?.on) return undefined;
    const handler = () => bumpTick();
    const events = [
      "member.added",
      "member.removed",
      "member.updated",
      "channel.updated",
      "notification.added_to_channel",
      "notification.removed_from_channel",
    ];
    const subs = events.map((evt) => channel.on(evt, handler));
    return () => subs.forEach((sub) => sub?.unsubscribe && sub.unsubscribe());
  }, [channel]);

  const data = channel?.data || null;
  const cid = stripMessagingPrefix(channel?.cid || channel?.id || null);

  // Pitch channels carry a numeric pitch id in the cid; keeps the modals on the
  // proven pitch endpoints for backward compatibility.
  const pitchId = useMemo(() => {
    const source = String(channel?.cid || channel?.id || "");
    const match = source.match(/pitch-(\d+)/);
    return match ? match[1] : null;
  }, [channel]);
  // Genuine club↔agent pitch only — agent-tender response chats reuse the
  // `pitch-<id>` cid but must not mount the club rail (they are anon agent↔agent).
  const isPitch = isClubAgentPitch(channel);

  const tpContext = data?._tp_custom_context?.tp_context || null;
  const isClubAgentChannel = tpContext === "club_agent" || isPitch;

  const currentId = toId(user?.account_id ?? user?.id);
  const isFrozen = data?.frozen === true;

  const clubIds = useMemo(
    () => (data?.club_member_account_ids || []).map((v) => toId(v)).filter(Boolean),
    [data?.club_member_account_ids],
  );
  const baseAdminId =
    toId(data?.club_admin_account_id) ||
    toId(data?.pitch_details?.club_admin_account_id);
  const adminId = adminOverride || baseAdminId;
  const agentId = toId(data?.agent_account_id);

  const hasClubStaffRole = useMemo(() => {
    const tokens = ["club_admin", "club admin", "clubadmin", "staff", "scout"];
    const candidates = [];
    if (currentRole) candidates.push(currentRole);
    if (Array.isArray(userRoles)) candidates.push(...userRoles);
    return candidates.some((role) =>
      tokens.includes(String(role || "").replace(/[^a-zA-Z ]/g, "").toLowerCase()),
    );
  }, [currentRole, userRoles]);

  // Merge channel.data.users with live Stream members (same dedup idea as
  // CustomChatHeader.combinedOtherUsers); both are keyed by account_id.
  const profilesById = useMemo(() => {
    const map = new Map();
    if (Array.isArray(data?.users)) {
      data.users.forEach((entry) => {
        const id = toId(entry?.id);
        if (id) map.set(id, { ...entry, id });
      });
    }
    Object.values(channel?.state?.members || {}).forEach((member) => {
      const su = member?.user;
      const id = toId(su?.id);
      if (!id) return;
      const existing = map.get(id) || { id };
      map.set(id, {
        id,
        name: existing.name || su.name || su.full_name,
        email: existing.email || su.email,
        role: existing.role || su.role,
        profile_picture_url:
          existing.profile_picture_url || su.profile_picture_url || su.image,
        agency_name: existing.agency_name || su.agency_name,
        club_name: existing.club_name || su.club_name,
        online: su.online,
      });
    });
    // Make sure the admin appears even if metadata is sparse.
    if (adminId && !map.has(adminId)) map.set(adminId, { id: adminId });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.users, channel?.state?.members, adminId, tick]);

  const haveMembershipMeta = clubIds.length > 0 || Boolean(baseAdminId);

  // A person is on the club side when membership metadata lists them (or they
  // are the admin); without metadata we fall back to role. The agent (agentId)
  // is never a club member — they are always the counterpart.
  const isClubSide = (id, profile) => {
    if (agentId && id === agentId) return false;
    if (haveMembershipMeta) return clubIds.includes(id) || id === adminId;
    return !isAgentRole(profile?.role);
  };

  const { counterparts, clubMembers } = useMemo(() => {
    const cps = [];
    const members = [];
    profilesById.forEach((profile, id) => {
      if (isClubSide(id, profile)) {
        members.push(profile);
      } else if (id !== currentId) {
        // The other side, pinned as the counterpart. The viewer is never their
        // own counterpart (5d bug): an agent viewing the thread would otherwise
        // see themselves pinned here — and must not fall into the club list
        // either, so the viewer's own row is simply dropped on the agent side.
        cps.push(profile);
      }
    });
    // Admin first, then alphabetical — matches the design ordering.
    members.sort((a, b) => {
      if (toId(a.id) === adminId) return -1;
      if (toId(b.id) === adminId) return 1;
      return pickName(a).localeCompare(pickName(b));
    });
    return { counterparts: cps, clubMembers: members };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profilesById, adminId, agentId, clubIds, haveMembershipMeta, currentId]);

  const isViewerClub = useMemo(() => {
    if (haveMembershipMeta) {
      return Boolean(currentId) && (clubIds.includes(currentId) || currentId === adminId);
    }
    if (agentId && currentId === agentId) return false;
    return hasClubStaffRole;
  }, [haveMembershipMeta, currentId, clubIds, adminId, agentId, hasClubStaffRole]);

  const isViewerAdmin = Boolean(currentId) && currentId === adminId;

  // Confirm the cid-based backend for non-pitch club-agent channels the viewer
  // could act on. The tp_context==club_agent gate above is the real "supported"
  // signal (the shipped route returns 200 for those, 400 for unsupported, 403
  // for non-club-staff); this probe just hides the controls if the route is
  // unreachable/rejects, so a stale or undeployed backend degrades gracefully.
  useEffect(() => {
    if (isPitch) {
      setDirectControlsSupported(true);
      return undefined;
    }
    if (!cid || !isClubAgentChannel || !isViewerClub || isFrozen) {
      return undefined;
    }
    let cancelled = false;
    setDirectControlsSupported(null);
    chatActionService.getChannelStaff(cid).then((res) => {
      if (cancelled) return;
      setDirectControlsSupported(Boolean(res.success));
    });
    return () => {
      cancelled = true;
    };
  }, [cid, isPitch, isClubAgentChannel, isViewerClub, isFrozen]);

  // D15: the "Chat details" rail (shared content) is reachable in EVERY thread —
  // so the rail mounts whenever a channel is open, not only club↔agent. The
  // participants / handover block below stays club↔agent-only; a plain DM shows
  // just the Shared section. Read-only/archived threads still render it (D15).
  if (!channel) return null;
  const showParticipants =
    isClubAgentChannel && (counterparts.length > 0 || clubMembers.length > 0);

  const controlsSupported = isPitch || directControlsSupported === true;
  const canAct = isViewerClub && !isFrozen && controlsSupported;
  const canAdd = canAct && isViewerAdmin;
  const canLeave =
    canAct && (isViewerAdmin ? clubMembers.length >= 2 : true);

  const reassignTarget = reassignTargetId
    ? clubMembers.find((m) => toId(m.id) === toId(reassignTargetId))
    : null;

  const infoDot = (size, borderColor, textColor, title) => (
    <span
      title={title}
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        border: `1.2px solid ${borderColor}`,
        color: textColor,
        background: "#F0F6FF",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size <= 13 ? 8.5 : 9,
        fontWeight: 700,
        fontFamily: "Georgia,serif",
        flexShrink: 0,
        cursor: "default",
      }}
    >
      i
    </span>
  );

  const renderPersonRow = (profile, { role, subLabel, admin, showYou, actions }) => {
    const id = toId(profile.id);
    const name = pickName(profile);
    return (
      <div
        key={id}
        className="flex items-center rounded-[9px]"
        style={{ gap: 9, padding: "7px 8px" }}
      >
        <Avatar
          src={pickAvatar(profile)}
          alt={name}
          size={32}
          fallbackText={name}
          className="flex-shrink-0"
        />
        <div className="flex flex-col min-w-0" style={{ flex: 1 }}>
          <div className="flex items-center" style={{ gap: 5 }}>
            <span
              className="truncate"
              style={{
                fontSize: 12.5,
                fontWeight: admin ? 700 : 600,
                color: NAME_COLOR,
              }}
            >
              {name}
              {showYou && (
                <span style={{ fontWeight: 500, color: "#98A2B3" }}> · you</span>
              )}
            </span>
            {role && (
              <span
                style={{
                  padding: "1px 6px",
                  borderRadius: 6,
                  background: "#F0F6FF",
                  border: "1px solid #D6E6FF",
                  fontSize: 9.5,
                  fontWeight: 600,
                  color: "#1466D8",
                  flexShrink: 0,
                }}
              >
                {role}
              </span>
            )}
          </div>
          <span className="truncate" style={{ fontSize: 10.5, color: "#98A2B3" }}>
            {subLabel}
          </span>
        </div>
        {admin && (
          <span
            style={{
              padding: "1px 7px",
              borderRadius: 999,
              background: "#F0F6FF",
              border: "1px solid #BBD7FF",
              fontSize: 9,
              fontWeight: 700,
              color: "#1466D8",
              letterSpacing: ".04em",
              flexShrink: 0,
            }}
          >
            ADMIN
          </span>
        )}
        {admin &&
          infoDot(
            13,
            "#C6D3E2",
            "#98A2B3",
            "The admin holds timer control and can hand the conversation over.",
          )}
        {actions}
      </div>
    );
  };

  // The rail content is shared between the desktop rail and the mobile sheet
  // (5f). On mobile the BottomSheet supplies the "In this conversation" title,
  // so the mono section header is desktop-only; the row "⋯" becomes a tappable
  // "Make admin" text button.
  const renderRailContent = (inSheet) => (
    <>
      {/* IN THIS CONVERSATION + info tooltip (desktop rail only, club↔agent) */}
      {showParticipants && !inSheet && (
      <div className="relative flex items-center" style={{ gap: 6 }}>
        <span style={SECTION_LABEL_STYLE}>IN THIS CONVERSATION</span>
        <button
          type="button"
          onClick={() => setShowTooltip((v) => !v)}
          onBlur={() => setShowTooltip(false)}
          style={{
            width: 14,
            height: 14,
            borderRadius: 999,
            border: "1.2px solid #2283FF",
            color: "#1466D8",
            background: "#F0F6FF",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 9,
            fontWeight: 700,
            fontFamily: "Georgia,serif",
            flexShrink: 0,
          }}
          aria-label="How participant handover works"
        >
          i
        </button>
        {showTooltip && (
          <div
            style={{
              position: "absolute",
              top: 20,
              left: 0,
              width: 232,
              background: "#101828",
              color: "#fff",
              borderRadius: 11,
              padding: "12px 14px",
              display: "flex",
              flexDirection: "column",
              gap: 7,
              boxShadow: "0 12px 32px rgba(16,24,40,.28)",
              zIndex: 6,
            }}
          >
            <span style={{ fontSize: 11.5, fontWeight: 700 }}>
              How participant handover works
            </span>
            <span
              style={{
                fontSize: 11,
                lineHeight: 1.6,
                color: "rgba(255,255,255,.82)",
              }}
            >
              · Add members from your club: they see the full history
              <br />· The ADMIN badge marks who controls the timer
              <br />· Leaving always hands admin + timer to a successor you choose
              <br />· The agent sees changes as chat events, nothing more
            </span>
          </div>
        )}
      </div>
      )}

      {/* Counterpart(s) — pinned, untouchable (club↔agent only) */}
      {showParticipants && counterparts.map((profile) => {
        const agency = pickAgency(profile);
        return (
          <div
            key={toId(profile.id)}
            className="flex items-center rounded-[9px]"
            style={{ gap: 9, padding: "7px 8px", background: "#F8FAFC" }}
          >
            <Avatar
              src={pickAvatar(profile)}
              alt={pickName(profile)}
              size={32}
              fallbackText={pickName(profile)}
              className="flex-shrink-0"
            />
            <div className="flex flex-col min-w-0" style={{ flex: 1 }}>
              <div className="flex items-center" style={{ gap: 5 }}>
                <span
                  className="truncate"
                  style={{ fontSize: 12.5, fontWeight: 700, color: NAME_COLOR }}
                >
                  {pickName(profile)}
                </span>
                <span
                  style={{
                    padding: "1px 6px",
                    borderRadius: 6,
                    background: "#F0F6FF",
                    border: "1px solid #D6E6FF",
                    fontSize: 9.5,
                    fontWeight: 600,
                    color: "#1466D8",
                    flexShrink: 0,
                  }}
                >
                  {isAgentRole(profile.role)
                    ? "Agent"
                    : humanizeRole(profile.role) || "Agent"}
                </span>
              </div>
              <span
                className="truncate"
                style={{ fontSize: 10.5, color: "#98A2B3" }}
              >
                {agency ? `Counterpart · ${agency}` : "Counterpart"}
              </span>
            </div>
          </div>
        );
      })}

      {/* YOUR CLUB / participants list (club↔agent only) */}
      {showParticipants && (isViewerClub ? (
        <>
          <div style={{ height: 1, background: "#F0F2F6" }} />
          <span style={SECTION_LABEL_STYLE}>
            YOUR CLUB · {clubMembers.length}
          </span>
          {clubMembers.map((profile) => {
            const id = toId(profile.id);
            const admin = id === adminId;
            const showYou = id === currentId;
            const makeAdmin = canAdd && !admin; // admin-only, per pitch rules
            return renderPersonRow(profile, {
              role: null,
              subLabel: humanizeRole(profile.role) || "Staff",
              admin,
              showYou,
              actions: !makeAdmin ? null : inSheet ? (
                // Mobile: a tappable text label (the ⋯ dots are too small to hit).
                <button
                  type="button"
                  onClick={() => setReassignTargetId(id)}
                  className="flex-shrink-0"
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#1466D8",
                    background: "transparent",
                    padding: 2,
                  }}
                  aria-label={`Make ${pickName(profile)} admin`}
                >
                  Make admin
                </button>
              ) : (
                <button
                  type="button"
                  title="Make admin"
                  onClick={() => setReassignTargetId(id)}
                  className="flex-shrink-0"
                  style={{ display: "flex", gap: 2, padding: 2 }}
                  aria-label={`Make ${pickName(profile)} admin`}
                >
                  <span style={{ width: 2.5, height: 2.5, borderRadius: 99, background: "#98A2B3" }} />
                  <span style={{ width: 2.5, height: 2.5, borderRadius: 99, background: "#98A2B3" }} />
                  <span style={{ width: 2.5, height: 2.5, borderRadius: 99, background: "#98A2B3" }} />
                </button>
              ),
            });
          })}
        </>
      ) : (
        // Agent side: slim read-only list, no actions (5d right side).
        <>
          {clubMembers.length > 0 && (
            <div style={{ height: 1, background: "#F0F2F6" }} />
          )}
          {clubMembers.map((profile) => {
            const id = toId(profile.id);
            const admin = id === adminId;
            return renderPersonRow(profile, {
              role: null,
              subLabel: humanizeRole(profile.role) || "Staff",
              admin,
              showYou: false,
              actions: null,
            });
          })}
        </>
      ))}

      {/* D15 Shared content (frames 5f / 5l) — Media · Documents · Links ·
          Business Cards for this thread. Rendered in EVERY thread type; a
          divider separates it from the participants block when one is shown. */}
      {showParticipants && <div style={{ height: 1, background: "#F0F2F6" }} />}
      <SharedContentSection
        channel={channel}
        selfId={currentId}
        variant={inSheet ? "sheet" : "rail"}
      />

      {/* Actions (club side only) */}
      {isViewerClub && (canAdd || canLeave) && (
        <div
          style={{
            marginTop: inSheet ? 4 : "auto",
            display: "flex",
            flexDirection: "column",
            gap: 7,
          }}
        >
          {canAdd && (
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                padding: "8px 12px",
                borderRadius: 9,
                border: "1px solid #BBD7FF",
                color: "#1466D8",
                fontSize: 12,
                fontWeight: 600,
                background: "#fff",
              }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M6 2v8M2 6h8"
                  stroke="#1466D8"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
              Add member
            </button>
          )}
          {canLeave && (
            <button
              type="button"
              onClick={() => setShowLeaveModal(true)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                padding: "8px 12px",
                borderRadius: 9,
                border: "1px solid #E4E9F0",
                color: "#667085",
                fontSize: 12,
                fontWeight: 600,
                background: "#fff",
              }}
            >
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                <path
                  d="M9 2.5H4a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h5M6.5 7H12M12 7L9.8 4.8M12 7l-2.2 2.2"
                  stroke="#667085"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Leave conversation
            </button>
          )}
        </div>
      )}
    </>
  );

  return (
    <>
      {/* Desktop rail (5a). The rail is a fixed 264px that never shrinks, so at
          768 it left the thread 184px — the composer collapsed to a 28px field
          and a 14px Send, and a 296px shared card hung 119px under the list.
          It now appears from lg, where there is room for all three columns.
          Below lg it is not lost: the header's "Chat details" trigger opens the
          same content in the bottom sheet below, and that trigger's breakpoint
          moves with this one (CustomChatHeader.js) — the two are a pair. */}
      <div
        className="hidden lg:flex flex-col flex-shrink-0"
        style={{
          width: 264,
          borderLeft: "1px solid #E4E9F0",
          background: "#fff",
          padding: 14,
          gap: 8,
          overflowY: "auto",
        }}
      >
        {renderRailContent(false)}
      </div>

      {/* Participants sheet (5f) — opened from the header avatar-stack. The rail
          above only appears from lg, so this sheet has to stay available up to
          lg as well; the shared sheet defaults to md and takes the exception
          through hiddenFrom. Without it the trigger would fire and the sheet
          would mount at 0×0 on a tablet — a visible button doing nothing. */}
      <BottomSheet
        open={mobileSheetOpen}
        hiddenFrom="lg"
        onClose={() => setMobileSheetOpen(false)}
        title={
          <span className="inline-flex items-center" style={{ gap: 7 }}>
            {/* D15: on a non-club↔agent thread the sheet is just Shared content,
                so it reads "Chat details" without the handover tooltip. */}
            {showParticipants ? "In this conversation" : "Chat details"}
            {showParticipants &&
              infoDot(
                15,
                "#C6D3E2",
                "#98A2B3",
                "Add members from your club, hand over the timer, or leave. The agent sees changes as chat events.",
              )}
          </span>
        }
      >
        <div className="flex flex-col" style={{ gap: 8, paddingBottom: 8 }}>
          {renderRailContent(true)}
        </div>
      </BottomSheet>

      {/* Handover modals — cid-based for direct channels, pitch-based for pitch */}
      <AddChatMembersModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        pitchId={pitchId}
        cid={cid}
        channelId={channel?.id}
        channel={channel}
      />
      <LeaveGroupModal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        pitchId={pitchId}
        cid={cid}
        channelId={channel?.id}
        channel={channel}
        currentAccountId={currentId}
        isChannelAdmin={isViewerAdmin}
      />
      <ReassignAdminModal
        isOpen={Boolean(reassignTargetId)}
        onClose={() => setReassignTargetId(null)}
        pitchId={pitchId}
        cid={cid}
        channelId={channel?.id}
        channel={channel}
        currentAdminAccountId={adminId}
        currentAccountId={currentId}
        preselectAccountId={reassignTargetId}
        title="Make admin"
        description={
          reassignTarget
            ? `${pickName(reassignTarget)} becomes admin of this conversation. Timer control passes to them.`
            : undefined
        }
        onAdminReassigned={(newAdminId) => {
          const normalized = toId(newAdminId);
          if (normalized) {
            setAdminOverride(normalized);
            if (channel?.data) channel.data.club_admin_account_id = normalized;
          }
          setReassignTargetId(null);
        }}
      />
    </>
  );
};

export default ParticipantsRail;
